import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { getUrl } from 'aws-amplify/storage';
import type { Schema } from '../amplify/data/resource';
import './CourseReferencesGrid.css';

interface Reference {
  id: string;
  name: string;
  website?: string;
  logoPath?: string;
  testimonial?: string;
  projectName?: string;
  projectDescription?: string;
  isActive: boolean;
}

interface CourseReferencesGridProps {
  courseId?: string;
  maxReferences?: number;
}

const CourseReferencesGrid: React.FC<CourseReferencesGridProps> = ({ 
  courseId, 
  maxReferences = 12 
}) => {
  const [references, setReferences] = useState<Reference[]>([]);
  const [referenceLogoUrls, setReferenceLogoUrls] = useState<{[key: string]: string}>({});
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);

  const client = generateClient<Schema>({ authMode: 'iam' });
  const referencesPerPage = 12;

  // Fetch references from database
  const fetchReferences = async () => {
    try {
      console.log('🔍 Fetching references for CourseReferencesGrid...');
      const result = await client.models.Reference.list({
        selectionSet: ['id', 'name', 'website', 'logoPath', 'testimonial', 'projectName', 'projectDescription', 'isActive']
      });
      
      if (result.data) {
        // Filter active references
        const activeReferences = result.data
          .filter((ref: any) => ref.isActive)
          .slice(0, maxReferences);
        
        console.log('✅ References fetched for CourseReferencesGrid:', activeReferences.length);
        setReferences(activeReferences as Reference[]);
      }
    } catch (err) {
      console.error('🚨 Error fetching references for CourseReferencesGrid:', err);
    }
  };

  // Handle reference logo click
  const handleReferenceClick = (reference: Reference) => {
    if (reference.website) {
      window.open(reference.website, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    fetchReferences();
  }, [courseId, maxReferences]);

  // Resolve reference logo URLs
  useEffect(() => {
    const resolveReferenceLogos = async () => {
      const logoUrls: {[key: string]: string} = {};
      
      for (const reference of references) {
        if (reference.logoPath) {
          try {
            if (reference.logoPath.startsWith('http') || reference.logoPath.startsWith('/')) {
              logoUrls[reference.id] = reference.logoPath;
            } else {
              // Try S3 URL resolution first (like admin components)
              try {
                const publicPath = reference.logoPath.startsWith('public/') ? reference.logoPath : `public/${reference.logoPath}`;
                const { url } = await getUrl({ path: publicPath });
                logoUrls[reference.id] = url.toString();
              } catch {
                // Fallback to legacy /assets path
                logoUrls[reference.id] = `/assets/images/${reference.logoPath}`;
              }
            }
          } catch (e) {
            console.warn(`Failed to resolve logo for reference ${reference.name}:`, e);
            logoUrls[reference.id] = '';
          }
        }
      }
      
      setReferenceLogoUrls(logoUrls);
    };

    if (references.length > 0) {
      resolveReferenceLogos();
    }
  }, [references]);

  // Pagination logic
  const totalPages = Math.ceil(references.length / referencesPerPage);
  const currentReferences = references.slice(
    currentPage * referencesPerPage,
    (currentPage + 1) * referencesPerPage
  );

  // Auto-pagination with loading bar
  useEffect(() => {
    if (totalPages <= 1) return;

    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          setIsTransitioning(true);
          setTimeout(() => {
            setCurrentPage(prevPage => (prevPage + 1) % totalPages);
            setIsTransitioning(false);
          }, 300);
          return 0;
        }
        return prev + 2; // 2% every 100ms = 5 seconds total
      });
    }, 100);

    return () => clearInterval(interval);
  }, [totalPages, currentPage]);


  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjVGNUY1IiBzdHJva2U9IiNFMEUwRTAiLz4KPHR5cGU+TG9nbzwvdHlwZT4KPC9zdmc+`;
    target.alt = 'Reference Logo Placeholder';
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
    return false;
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  if (references.length === 0) {
    return null; // Don't render if no references
  }

  return (
    <div className="course-references-card">
      <h4>Referenzen</h4>
      <div className="course-references-pagination-container">
        <div className={`course-references-grid ${isTransitioning ? 'transitioning' : ''}`}>
          {currentReferences.map((reference) => {
            const logoUrl = referenceLogoUrls[reference.id];
            if (!logoUrl) return null;
            
            return (
              <div 
                key={reference.id} 
                className={`course-reference-grid-item ${reference.website ? 'clickable' : ''}`}
                onClick={() => handleReferenceClick(reference)}
                title={reference.website ? `Besuchen Sie ${reference.name}` : reference.name}
              >
                <img
                  src={logoUrl}
                  alt={`${reference.name} Logo`}
                  className="course-reference-grid-image"
                  onError={handleImageError}
                  onDragStart={handleDragStart}
                  onContextMenu={handleContextMenu}
                  draggable={false}
                />
              </div>
            );
          })}
        </div>
        
        {totalPages > 1 && (
          <div className="course-references-loading-bar-container">
            <div 
              className="course-references-loading-bar"
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseReferencesGrid;
